import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Chip,
  Grid,
  Paper,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { metadataSuccess } from "app-component/slices/metadata-slice";
import { getLanguageListData } from "utils/metadata-split/metaDataApiFetch";
import {
  getUpdatePageStatelanguagePrefListRequest,
  languageChoiceListRequest,
  setFilterAfterUpdate,
} from "pages/configue/slices/language-preferance-slice";
import { useHistory, useRouteMatch } from "react-router-dom";
import { isEmpty } from "utils/helpers";
import ContentRight from "shared/molecules/content-right";
import LoadingButton from "@mui/lab/LoadingButton";
import configueService from "pages/configue/service";
import { snackbarRequest } from "shared/molecules/snackbar/snackbar-slice";
import { areAllFieldsFilled } from "./helper";
import CreateDataTable from "./table/create-table";
import Loader from "pages/build-test/loader";

const theme = createTheme();

const ConfigLanguagePreferenceSelector = () => {
  // State to manage final mapped data
  const [mappedData, setMappedData] = useState<any>([]);
  const match: any = useRouteMatch();
  const [showLoading, setShowIsLoading] = useState(true);
  const [btnLoading, setBtnLoading] = useState(false);
  const history = useHistory();

  const { isLoading: isLoad, data: metaData }: any = useAppSelector(
    (state) => state.metadataReducer
  );

  const { updatePageStateLangPrefList, languageChoiceList, loading }: any =
    useAppSelector((state) => state.languagePreferanceSlice);

  const dispatch = useAppDispatch();

  // State to manage selections for each configuration item
  const [selections, setSelections] = useState({});

  // Initialize selections state
  useEffect(() => {
    if (!isEmpty(languageChoiceList)) {
      const initialSelections = {};
      languageChoiceList?.forEach((item) => {
        initialSelections[item?.objectId] = item?.SubjectObjectId || "";
      });
      setSelections(initialSelections);
    }
  }, [languageChoiceList]);

  // Update mapped data whenever selections change
  useEffect(() => {
    if (!isEmpty(selections)) {
      const newMappedData = languageChoiceList?.map((configItem) => {
        const selectedSubjectId = selections[configItem.objectId];
        const selectedSubject = metaData?.language_list?.find(
          (subject) => subject.objectId === selectedSubjectId
        );

        return {
          language_subject_choice_id: configItem.objectId,
          StateLanguagePreferenceObjectId:
            configItem.StateLanguagePreferenceObjectId,
          StateLanguagePreferenceSubjectName:
            configItem.StateLanguagePreferenceSubjectName,
          PreferenceType: configItem.PreferenceType,
          subject_id: selectedSubject ? selectedSubject.objectId : null,
          subject_name: selectedSubject ? selectedSubject.subject_name : null,
          createdAt: configItem.createdAt,
          updatedAt: configItem.updatedAt,
          createdById: configItem.createdById,
          updatedById: configItem.updatedById,
          isArchived: configItem.isArchived,
        };
      });

      setMappedData(newMappedData);
    }
  }, [selections]);

  // Handle selection change
  const handleSelectionChange = (configObjectId, selectedSubjectId) => {
    setSelections((prev) => ({
      ...prev,
      [configObjectId]: selectedSubjectId,
    }));
  };

  const updateMetaData = (listData, metaKey) => {
    let metadataList = { ...metaData };
    metadataList[metaKey] = listData;
    dispatch(metadataSuccess(metadataList));
    setShowIsLoading(false);
  };

  const handleConfig = async () => {
    try {
      setBtnLoading(true);
      const res: any = await configueService.ConfigureLanguageChoice({
        language_subject_choice_list: mappedData,
      });
      if (res?.status === 200 && res?.data?.status === "success") {
        dispatch(snackbarRequest({ message: res?.data?.message }));
        setBtnLoading(false);
        const payload = {
          state_id: updatePageStateLangPrefList?.State,
          board_id: updatePageStateLangPrefList?.Board,
          segment_id: updatePageStateLangPrefList?.Segment,
          class_id: updatePageStateLangPrefList?.Class,
        };
        dispatch(setFilterAfterUpdate(payload));
        history.push(`/configue/language_preferance`);
      } else {
        setBtnLoading(false);
        dispatch(snackbarRequest({ message: res.data.message, type: "error" }));
      }
    } catch (error: any) {
      setBtnLoading(false);
      dispatch(
        snackbarRequest({
          message: error?.response?.data?.message,
          type: "error",
        })
      );
    }
  };

  useEffect(() => {
    setShowIsLoading(true);
    (async () => {
      try {
        const call1: any = await getLanguageListData(updateMetaData);
        updateMetaData(call1[0], call1[1]);
      } catch (error) {
        console.log(error);
      }
    })();
  }, []);

  useEffect(() => {
    dispatch(
      languageChoiceListRequest({
        state_language_preference_id:
          match?.params?.state_language_preference_id,
      })
    );
    dispatch(
      getUpdatePageStatelanguagePrefListRequest({
        state_language_preference_id:
          match?.params?.state_language_preference_id,
      })
    );
  }, []);

  // Get preference type color
  const getPreferenceColor = (type) => {
    switch (type) {
      case "first":
        return "primary";
      case "second":
        return "secondary";
      case "third":
        return "success";
      default:
        return "default";
    }
  };

  if (showLoading) {
    return <Loader />;
  }
  if (!showLoading && loading) {
    return <Loader />;
  }

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ p: 3,width:"98%" }}>
        <Typography variant="h4" gutterBottom>
          Language Preference Configuration
        </Typography>

         { 
        !loading &&
        !isEmpty(updatePageStateLangPrefList) && (
          <Box
            sx={{
              overflowX: "auto",
              overflowY: "visible",
              width: "100%",
              marginBottom:"30px"
            }}
          >
            <CreateDataTable data={[updatePageStateLangPrefList]} />
          </Box>
        )}

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Configuration Items
            </Typography>

            {languageChoiceList?.map((item) => {
              return (
                <Card
                  key={item.objectId}
                  sx={{
                    mb: 2,
                    boxShadow: 2,
                    border: "1px solid #e0e0e0",
                    borderRadius: 2,
                    "&:hover": {
                      boxShadow: 3,
                    },
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 3,
                      }}
                    >
                      {/* Left Side - Subject Information */}
                      <Box sx={{ flex: "0 0 40%", minWidth: 0 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            mb: 2,
                            flexWrap: "wrap",
                            gap: 1,
                          }}
                        >
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 600,
                              color: "primary.main",
                              fontSize: "1.1rem",
                            }}
                          >
                            {item.StateLanguagePreferenceSubjectName}
                          </Typography>
                          <Chip
                            label={item.PreferenceType}
                            color={getPreferenceColor(item.PreferenceType)}
                            size="small"
                            sx={{
                              fontWeight: 500,
                              textTransform: "capitalize",
                            }}
                          />
                        </Box>

                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            fontSize: "0.875rem",
                            fontFamily: "monospace",
                            backgroundColor: "#f5f5f5",
                            padding: "4px 8px",
                            borderRadius: 1,
                            display: "inline-block",
                          }}
                        >
                          ID: {item.objectId}
                        </Typography>
                      </Box>

                      {/* Right Side - Form Control */}
                      <Box sx={{ flex: "0 0 60%", maxWidth: "60%", pr: 3 }}>
                        <FormControl
                          fullWidth
                          size="medium"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                              "&:hover .MuiOutlinedInput-notchedOutline": {
                                borderColor: "primary.main",
                              },
                              "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                {
                                  borderWidth: 2,
                                },
                            },
                            "& .MuiInputLabel-root": {
                              fontWeight: 500,
                            },
                          }}
                        >
                          <InputLabel>Select Subject Variant</InputLabel>
                          <Select
                            value={selections[item.objectId] || ""}
                            label="Select Subject Variant"
                            onChange={(e) =>
                              handleSelectionChange(
                                item.objectId,
                                e.target.value
                              )
                            }
                            MenuProps={{
                              PaperProps: {
                                style: {
                                  //maxHeight: 200,
                                  borderRadius: 8,
                                },
                              },
                            }}
                          >
                            <MenuItem
                              value=""
                              sx={{
                                fontStyle: "italic",
                                color: "text.secondary",
                              }}
                            >
                              <em>None</em>
                            </MenuItem>
                            {metaData?.language_list?.map((option) => (
                              <MenuItem
                                key={option.objectId}
                                value={option.objectId}
                                sx={{
                                  "&:hover": {
                                    backgroundColor: "primary.light",
                                    color: "primary.contrastText",
                                  },
                                }}
                              >
                                {option.subject_name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              );
            })}
          </Grid>
        </Grid>
      </Box>

      <ContentRight sx={{ mt: -4 }}>
        <LoadingButton
          variant="contained"
          loadingPosition="start"
          loading={btnLoading}
          sx={{
            width: "250px",
            height: "40px",
            marginTop: "15px",
            marginRight: "40px",
            padding: "7px 42.5px 7px 48.5px",
            cursor: "pointer",
            textAlign: "center",
          }}
          onClick={handleConfig}
          disabled={!areAllFieldsFilled([selections])}
        >
          Configure Choices
        </LoadingButton>
      </ContentRight>
    </ThemeProvider>
  );
};

export default ConfigLanguagePreferenceSelector;
